/**
 * Dynamic Date Utility
 *
 * Resolves dynamic date placeholders in list configurations.
 *
 * Supported formats:
 * - Legacy: {{TODAY}}, {{YESTERDAY}}, {{ONE_WEEK_AGO}}
 * - Datetime: {{NOW}} (returns current ISO datetime string)
 * - Offset: {{DATE:+5d}}, {{DATE:-2w}}, {{DATE:+3m}}, {{DATE:-1y}}
 * - Anchors: {{DATE:start_of_week}}, {{DATE:end_of_month}}, etc.
 * - Combined: {{DATE:start_of_month:-1m}} (start of last month)
 * - Month names: {{MONTH}}, {{MONTH:-1m}}, {{MONTH:-2m}}, {{MONTH:+1m}}
 */

import {
	add,
	endOfMonth,
	endOfQuarter,
	endOfWeek,
	endOfYear,
	format,
	startOfMonth,
	startOfQuarter,
	startOfWeek,
	startOfYear,
	sub,
} from 'date-fns';

// Short month name array for {{MONTH}} placeholders
const MONTH_NAMES_SHORT = [
	'Jan',
	'Feb',
	'Mar',
	'Apr',
	'May',
	'Jun',
	'Jul',
	'Aug',
	'Sep',
	'Oct',
	'Nov',
	'Dec',
];

/**
 * Get month name (3-char abbreviation) with optional offset
 * @param offset - Number of months to offset (negative for past, positive for future)
 */
function getMonthName(offset: number = 0): string {
	const date = new Date();

	if (offset !== 0) {
		date.setMonth(date.getMonth() + offset);
	}

	return MONTH_NAMES_SHORT[date.getMonth()];
}

type DurationUnit = 'd' | 'w' | 'm' | 'y';

interface ParsedOffset {
	value: number;
	unit: DurationUnit;
}

const ANCHOR_FUNCTIONS: Record<string, (date: Date) => Date> = {
	start_of_week: startOfWeek,
	end_of_week: endOfWeek,
	start_of_month: startOfMonth,
	end_of_month: endOfMonth,
	start_of_quarter: startOfQuarter,
	end_of_quarter: endOfQuarter,
	start_of_year: startOfYear,
	end_of_year: endOfYear,
};

const UNIT_MAP: Record<DurationUnit, string> = {
	d: 'days',
	w: 'weeks',
	m: 'months',
	y: 'years',
};

/**
 * Parse an offset string like "+5d", "-2m", "0d"
 */
function parseOffset(offsetStr: string): ParsedOffset | null {
	const match = offsetStr.match(/^([+-]?\d+)([dwmy])$/);

	if (!match) {
		return null;
	}

	const [, valueStr, unit] = match;

	return {
		value: parseInt(valueStr, 10),
		unit: unit as DurationUnit,
	};
}

/**
 * Apply an offset to a date
 */
function applyOffset(date: Date, offset: ParsedOffset): Date {
	const duration = { [UNIT_MAP[offset.unit]]: Math.abs(offset.value) };

	if (offset.value >= 0) {
		return add(date, duration);
	}

	return sub(date, duration);
}

/**
 * Format a date to YYYY-MM-DD string
 */
function formatDate(date: Date): string {
	return format(date, 'yyyy-MM-dd');
}

/**
 * Check if a string contains any dynamic date placeholder
 */
export function containsDynamicDate(value: string): boolean {
	if (typeof value !== 'string') {
		return false;
	}

	return (
		value.includes('{{TODAY}}') ||
		value.includes('{{YESTERDAY}}') ||
		value.includes('{{ONE_WEEK_AGO}}') ||
		value.includes('{{NOW}}') ||
		value.includes('{{DATE:') ||
		value.includes('{{MONTH}}') ||
		value.includes('{{MONTH:')
	);
}

/**
 * Resolve a single dynamic date expression
 * Returns the original value if not a dynamic date expression
 */
export function resolveDynamicDate(value: string): string {
	if (typeof value !== 'string') {
		return value;
	}

	const today = new Date();

	// Handle legacy placeholders
	if (value === '{{TODAY}}') {
		return formatDate(today);
	}

	if (value === '{{YESTERDAY}}') {
		return formatDate(sub(today, { days: 1 }));
	}

	if (value === '{{ONE_WEEK_AGO}}') {
		return formatDate(sub(today, { weeks: 1 }));
	}

	// Handle {{NOW}} - returns current datetime as ISO string
	if (value === '{{NOW}}') {
		return new Date().toISOString();
	}

	// Handle new syntax: {{DATE:...}}
	const match = value.match(/^\{\{DATE:(.+)\}\}$/);

	if (!match) {
		return value;
	}

	const expression = match[1];

	// Check for combined syntax: anchor:offset (e.g., "start_of_month:-1m")
	const combinedMatch = expression.match(/^([a-z_]+):([+-]?\d+[dwmy])$/);

	if (combinedMatch) {
		const [, anchor, offsetStr] = combinedMatch;
		const anchorFn = ANCHOR_FUNCTIONS[anchor];

		if (!anchorFn) {
			console.warn(`Unknown date anchor: ${anchor}`);

			return value;
		}

		const offset = parseOffset(offsetStr);

		if (!offset) {
			console.warn(`Invalid date offset: ${offsetStr}`);

			return value;
		}

		// Apply offset first, then anchor
		const offsetDate = applyOffset(today, offset);

		return formatDate(anchorFn(offsetDate));
	}

	// Check for anchor only (e.g., "start_of_week")
	if (ANCHOR_FUNCTIONS[expression]) {
		return formatDate(ANCHOR_FUNCTIONS[expression](today));
	}

	// Check for offset only (e.g., "+5d", "-2m", "0d")
	const offset = parseOffset(expression);

	if (offset) {
		return formatDate(applyOffset(today, offset));
	}

	console.warn(`Invalid dynamic date expression: ${expression}`);

	return value;
}

/**
 * Replace all dynamic date placeholders in a string
 * Handles cases where placeholders are embedded in larger strings
 */
export function replaceDynamicDates(value: string): string {
	if (typeof value !== 'string') {
		return value;
	}

	let result = value;

	// Replace legacy placeholders
	result = result.replace(/\{\{TODAY\}\}/g, formatDate(new Date()));
	result = result.replace(
		/\{\{YESTERDAY\}\}/g,
		formatDate(sub(new Date(), { days: 1 })),
	);
	result = result.replace(
		/\{\{ONE_WEEK_AGO\}\}/g,
		formatDate(sub(new Date(), { weeks: 1 })),
	);

	// Replace {{NOW}} with current ISO datetime
	result = result.replace(/\{\{NOW\}\}/g, new Date().toISOString());

	// Replace new syntax placeholders
	const datePattern = /\{\{DATE:([^}]+)\}\}/g;
	result = result.replace(datePattern, (match, expression) => {
		const resolved = resolveDynamicDate(`{{DATE:${expression}}}`);

		// If resolution failed, return original match
		return resolved === `{{DATE:${expression}}}` ? match : resolved;
	});

	// Replace {{MONTH}} (current month name)
	result = result.replace(/\{\{MONTH\}\}/g, getMonthName(0));

	// Replace {{MONTH:+Nm}} or {{MONTH:-Nm}} (month name with offset)
	result = result.replace(/\{\{MONTH:([+-]?\d+)m\}\}/g, (match, offsetStr) => {
		const offset = parseInt(offsetStr, 10);

		return getMonthName(offset);
	});

	return result;
}

/**
 * Process an object, replacing all dynamic date placeholders in string values
 */
export function processDynamicDatesInObject<T>(obj: T): T {
	if (obj == null) {
		return obj;
	}

	if (typeof obj === 'string') {
		return replaceDynamicDates(obj) as T;
	}

	if (Array.isArray(obj)) {
		return obj.map((item) => processDynamicDatesInObject(item)) as T;
	}

	if (typeof obj === 'object') {
		const result: Record<string, unknown> = {};

		for (const [key, value] of Object.entries(obj)) {
			result[key] = processDynamicDatesInObject(value);
		}

		return result as T;
	}

	return obj;
}

/**
 * Get the timezone offset in minutes for a given timezone at a specific date
 * @param date - The date to check the offset for (important for DST)
 * @param timezone - IANA timezone identifier (e.g., 'America/New_York')
 * @returns Offset in minutes (positive = behind UTC, negative = ahead of UTC)
 */
export function getTimezoneOffset(date: Date, timezone: string): number {
	// Format date in the target timezone and in UTC, then compare
	const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
	const tzDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));

	return (utcDate.getTime() - tzDate.getTime()) / 60000;
}

/**
 * Get the browser's current timezone
 * @returns IANA timezone identifier
 */
export function getBrowserTimezone(): string {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone;
	} catch {
		return 'America/New_York'; // Default fallback
	}
}

/**
 * Resolve a dynamic date expression to the START of that day in the user's timezone,
 * returned as a full ISO datetime string in UTC.
 *
 * Example: For "today" in America/New_York (ET), returns the UTC time equivalent
 * to midnight ET on that day.
 *
 * @param value - Dynamic date expression (e.g., '{{DATE:0d}}', '{{TODAY}}', '{{DATE:start_of_week}}')
 * @param timezone - IANA timezone identifier (defaults to browser timezone)
 * @returns Full ISO datetime string (e.g., '2024-12-16T05:00:00.000Z')
 */
export function resolveDynamicDateTimeStart(
	value: string,
	timezone?: string,
): string {
	const tz = timezone ?? getBrowserTimezone();

	// First resolve to get the date in YYYY-MM-DD format
	const dateStr = resolveDynamicDate(value);

	// If it didn't resolve (returned original value), return as-is
	if (dateStr === value && value.includes('{{')) {
		return value;
	}

	// Parse the date string as a local date
	const [year, month, day] = dateStr.split('-').map(Number);

	// Create a date object representing midnight in the target timezone
	// We need to figure out what UTC time corresponds to midnight in the target TZ
	const localMidnight = new Date(year, month - 1, day, 0, 0, 0, 0);

	// Get the offset for this date in the target timezone
	const offsetMinutes = getTimezoneOffset(localMidnight, tz);

	// Adjust to get the UTC time that corresponds to midnight in the target TZ
	const utcMidnight = new Date(localMidnight.getTime() + offsetMinutes * 60000);

	return utcMidnight.toISOString();
}

/**
 * Resolve a dynamic date expression to the END of that day in the user's timezone,
 * returned as a full ISO datetime string in UTC.
 *
 * Example: For "today" in America/New_York (ET), returns the UTC time equivalent
 * to 23:59:59.999 ET on that day.
 *
 * @param value - Dynamic date expression (e.g., '{{DATE:0d}}', '{{TODAY}}', '{{DATE:end_of_week}}')
 * @param timezone - IANA timezone identifier (defaults to browser timezone)
 * @returns Full ISO datetime string (e.g., '2024-12-17T04:59:59.999Z')
 */
export function resolveDynamicDateTimeEnd(
	value: string,
	timezone?: string,
): string {
	const tz = timezone ?? getBrowserTimezone();

	// First resolve to get the date in YYYY-MM-DD format
	const dateStr = resolveDynamicDate(value);

	// If it didn't resolve (returned original value), return as-is
	if (dateStr === value && value.includes('{{')) {
		return value;
	}

	// Parse the date string as a local date
	const [year, month, day] = dateStr.split('-').map(Number);

	// Create a date object representing 23:59:59.999 in the target timezone
	const localEndOfDay = new Date(year, month - 1, day, 23, 59, 59, 999);

	// Get the offset for this date in the target timezone
	const offsetMinutes = getTimezoneOffset(localEndOfDay, tz);

	// Adjust to get the UTC time that corresponds to end of day in the target TZ
	const utcEndOfDay = new Date(localEndOfDay.getTime() + offsetMinutes * 60000);

	return utcEndOfDay.toISOString();
}
